/* eslint-disable @typescript-eslint/no-shadow */

/* eslint-disable no-shadow */
import React from 'react';
import groupBy from 'lodash/groupBy';
import { VariableSizeList } from 'react-window';

import { SharedMenuItemsProps } from '../MenuItemsV3';
import GroupRowV3 from './GroupRowV3';
import UngroupedRowV3 from './UngroupedRowV3';

const HEADER_HEIGHT = 45;
const OPTION_HEIGHT = 49;

const getGroupHeight = group => {
  const [groupName, items] = group;
  const itemsHeight = items.length * OPTION_HEIGHT;

  return groupName ? itemsHeight + HEADER_HEIGHT : itemsHeight;
};

// Taken from MUI example: https://mui.com/material-ui/react-autocomplete/#virtualization
function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

function getGroupItemKey(index, { items }) {
  const item = items[index];
  return item[0];
}

function VirtualizedMenuItems<Item>({
  items,
  renderItem,
  itemToGroup,
}: SharedMenuItemsProps<Item>): React.ReactElement<SharedMenuItemsProps<Item>> {
  const groupedItems = React.useMemo(() => {
    return groupBy(items, itemToGroup);
  }, [items, itemToGroup]);
  const groupedItemsArray = React.useMemo(() => {
    return Object.entries(groupedItems);
  }, [groupedItems]);

  const itemCount = React.useMemo(() => {
    return groupedItemsArray.map(([, items]) => items.length).reduce((a, b) => a + b, 0);
  }, [groupedItemsArray]);
  const gridRef = useResetCache(itemCount);

  const getMenuHeight = () => {
    if (itemCount > 10) {
      return 500;
    }

    const fullHeight = groupedItemsArray
      .map(group => getGroupHeight(group))
      .reduce((a, b) => a + b, 0);

    return Math.min(fullHeight, 500);
  };

  if (!itemToGroup) {
    return (
      <VariableSizeList
        itemData={{
          items,
          renderItem,
        }}
        innerElementType="ul"
        role="listbox"
        width="800px"
        height={getMenuHeight()}
        ref={gridRef}
        itemCount={items.length}
        itemSize={() => OPTION_HEIGHT}
      >
        {UngroupedRowV3}
      </VariableSizeList>
    );
  }

  return (
    <VariableSizeList
      itemData={{
        items: groupedItemsArray,
        renderItem,
      }}
      innerElementType="ul"
      role="listbox"
      width="800px"
      height={getMenuHeight()}
      ref={gridRef}
      itemCount={groupedItemsArray.length}
      itemSize={index => {
        const group = groupedItemsArray[index];
        if (!group) {
          return 0;
        }
        return getGroupHeight(group);
      }}
      itemKey={getGroupItemKey}
    >
      {GroupRowV3}
    </VariableSizeList>
  );
}

export default VirtualizedMenuItems;
